// import { inspect } from '@xstate/inspect'
import { useInterpret } from '@xstate/react'
import { ComponentChildren, createContext } from 'preact'
import { StateUpdater, useContext, useState } from 'preact/hooks'
import { InterpreterFrom, StateFrom } from 'xstate'

import { buildEmailTemplateModel, EmailPayloadParameters } from '../lib/buildEmailPayload'
import { calculatorMachine } from '../machines/calculator.machine'
import { EmailReportContext, emailReportMachine } from '../machines/emailReport.machine'

// if (process.env.NODE_ENV !== 'production') {
//   inspect({
//     iframe: false, // open in new window
//   })
// }

type NaturalPowerServices = {
  costInput: InterpreterFrom<typeof calculatorMachine>
  emailReport: InterpreterFrom<typeof emailReportMachine>
  naturalPower: [number, StateUpdater<number>]
}
const NaturalPowerContext = createContext<NaturalPowerServices | undefined>(undefined)
export const useNaturalPowerContext = () => {
  const c = useContext(NaturalPowerContext)
  if (c === undefined) {
    throw new Error('useNaturalPowerContext must be used within a NaturalPowerProvider')
  }
  return c
}

export type InitialValues = { costInput: EmailPayloadParameters; emailReport: EmailReportContext }

interface NaturalPowerProviderProps {
  initialValues: InitialValues
  children: ComponentChildren
}

export function NaturalPowerCalculatorProvider({ children, initialValues }: NaturalPowerProviderProps) {
  const naturalPower = useState(initialValues.costInput.naturalPowerRatio)
  const emailReport = useInterpret(emailReportMachine, { context: initialValues.emailReport })
  const costInput = useInterpret(calculatorMachine, {
    context: { ...initialValues.costInput, emailRef: emailReport },
  })
  const services = {
    naturalPower,
    costInput,
    emailReport,
  }

  return <NaturalPowerContext.Provider value={services}>{services && children}</NaturalPowerContext.Provider>
}

export function useNaturalPowerRatio() {
  const { naturalPower } = useNaturalPowerContext()
  return naturalPower[0]
}

export const annualConsumptionSelector = (state: StateFrom<typeof calculatorMachine>) => state.context.annualConsumption
export const naturalPowerSelector = (state: StateFrom<typeof calculatorMachine>) => state.context.naturalPowerRatio
export const userInputSelector = (state: StateFrom<typeof calculatorMachine>) => {
  const { naturalPowerRatio, currentCost, paymentFrequency, annualConsumption } = state.context
  return { naturalPowerRatio, currentCost, paymentFrequency, annualConsumption }
}
export const costInputsSelector = (state: StateFrom<typeof calculatorMachine>) => {
  const {
    costPerUnit,
    gst,
    currentCost,
    paymentFrequency,
    annualConsumption,
    annualConsumptionOptions,
    naturalPowerRatio,
  } = state.context
  const averageCost = annualConsumptionOptions.find(({ value }) => value === annualConsumption)?.data?.averageCost || 0
  const currentAvarageCost = Number(currentCost) || averageCost
  const annualCost = currentAvarageCost * paymentFrequency
  const estimatedPremium = costPerUnit * annualConsumption * naturalPowerRatio * gst
  const estimatedTotal = estimatedPremium + annualCost

  return {
    paymentFrequency,
    currentCost: Number(currentCost),
    estimatedPremium,
    estimatedTotal,
  }
}

export const differenceSelector = (state: StateFrom<typeof calculatorMachine>) => {
  const { annualConsumption, naturalPowerRatio, emissionFactor, carEmissionFactor } = state.context
  const co2Saved = (annualConsumption * naturalPowerRatio * emissionFactor) / 1000
  const carsRemoved = co2Saved / carEmissionFactor

  return {
    co2Saved,
    carsRemoved,
  }
}

export const emailPayloadSelector = (state: StateFrom<typeof calculatorMachine>) =>
  buildEmailTemplateModel(state.context)

export const emailInputSelector = (state: StateFrom<typeof emailReportMachine>) => {
  const { emailAddress, message } = state.context
  const enabled = state.matches('Enabled')
  const valid = state.matches('Enabled.Valid email address')
  const sending = state.matches('Enabled.Valid email address.Sending email')
  const successful = state.matches('Enabled.Valid email address.Success')
  const canRetry = state.can({ type: 'Retry' })
  return { emailAddress, successful, message, enabled, valid, sending, canRetry }
}
